import {combineLatest as observableCombineLatest, fromEvent, Observable, of, of as observableOf} from 'rxjs';
import {
    catchError,
    concatMap,
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    mergeMap,
    switchMap,
    take,
    tap,
    throttleTime,
    withLatestFrom,
} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {AuthActionTypes} from './auth.actions';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Action, select, Store} from '@ngrx/store';
import {MatLegacyDialog as MatDialog} from '@angular/material/legacy-dialog';

import {AuthenticationService} from '@app/core/service/authentication.service';
import {AuthenticatedUserService} from '@app/core/service/authenticated-user.service';
import * as auth from './auth.actions';
import {UserPreferencesService} from '@app/core/service/user-preferences.service';
import {
    IntegrationClearSessionStoredData,
    IntegrationClearState,
    IntegrationGettingDefaultAction,
    GetSchoolYearRolloverSettingsAction,
    UpdateVendorCurrentAppAction
} from '@app/core/reducers/integration/integration.actions';
import {TokenService} from '@app/core/security/token.service';
import {ApplicationClearState} from '@app/views/admin/application-settings/store/application-settings.actions';
import {Preferences} from '@app/core/model/user-preferences.model';
import {getAuthenticatedUser, State, getUserOrganization, getAuthenticatedUserApplications} from '@app/app.reducers';
import {environment} from '@env/environment';
import {User, UserStatus} from '@app/core/model/user';
import {DataBrowser} from '@app/views/databrowsing/data-browser';
import {OrgsService} from '@app/core/service/orgs.service';
import {DirectoryOrganization} from '@app/core/model/entity-profile';
import * as ApplicationSettingsActions from '@app/views/admin/application-settings/store/application-settings.actions';
import {getApplicationSettings} from '@app/views/admin/application-settings/store/application-settings.reducers';
import * as fromApplicationSettings from '@app/views/admin/application-settings/store/application-settings.reducers';
import {ApplicationSettingsService} from '@app/views/admin/application-settings/application-settings.service';
import {Application} from '@app/views/admin/application-settings/models/application.model';
import {AuthenticatedResult} from 'angular-auth-oidc-client/lib/auth-state/auth-result';
import {FeatureTogglesService} from '@app/core/service/feature-toggles.service';
import {FeatureToggleModel} from '@app/views/common/feature-toggles/featue-toggle.model';
import {OidcSecurityProxyService} from '@app/core/service/oidc.security.proxy.service';
import {PIIShieldLevelConfig} from '@app/views/admin/application-settings/components/basic/pii-shield-level-info/pii-shield.model';
import {PIIShieldService} from '@app/core/service/pii-shield.service';
import {SSOPortalService} from '@app/views/sso/sso-portal.service';
import {PassportLoginIDP} from '@app/views/sso/identity-providers/idp-settings/passport-login-idp/passport-login-idp.model';
import {HttpErrorResponse, HttpResponseBase} from '@angular/common/http';
import {MixpanelService} from '@app/core/service/mixpanel.service';
import {ApplicationsService} from '@app/views/admin/provider/applications/applications.service';

@Injectable()
export class AuthEffects {
    /**
     * Authenticate user.
     * @method authenticate$
     */
    authenticate$: Observable<Action> = createEffect(() =>
        this.actions.pipe(
            ofType(AuthActionTypes.AUTHENTICATE),
            debounceTime(500),
            map((action: auth.AuthenticateAction) => action.payload),
            switchMap((payload: { username: string; password?: string; mfaCode?: string; resetCode?: string; errorMessage: string }) =>
                this.authService.login(payload.username, payload.password, payload.mfaCode, payload.resetCode).pipe(
                    mergeMap((response: HttpResponseBase) => {

                        let mfaPhone: string,
                            actions: Action[];

                        if ((response.status >= 200) && (response.status < 300)) {

                            const xMFAHeaderValue = response.headers.get('X-MFA');
                            if (xMFAHeaderValue) {
                            // MFA
                                if (xMFAHeaderValue === 'Activate') {
                                    // login with setup MFA
                                    this.tokenService.setToken(response.headers.get('X-Auth-Token'), true);

                                    const user = new User();
                                    user.needToSetupMFA = true;
                                    user.username = payload.username;

                                    actions = [
                                        new IntegrationClearState(),
                                        new auth.AuthenticationMFAFirstSuccessAction(user)
                                    ];

                                } else {
                                    // login with MFA
                                    this.tokenService.setToken(response.headers.get('X-Auth-Token'), true);
                                    mfaPhone = response.headers.get('X-MFA-Number');

                                    const user = new User();
                                    user.mfaPhone = mfaPhone;
                                    user.mfaEnabled = true;
                                    user.username = payload.username;

                                    actions = [
                                        new IntegrationClearState(),
                                        new auth.AuthenticationMFAFirstSuccessAction(user)
                                    ];
                                }

                            } else {

                                // login without MFA
                                if (response.headers.has('X-Auth-Token')) {
                                    this.tokenService.removeToken();
                                    this.tokenService.setToken(response.headers.get('X-Auth-Token'));
                                }

                                actions = [
                                    new IntegrationClearState(),
                                    new auth.AuthenticationSuccessAction()
                                ];
                            }

                        } else {
                            throw Error();
                        }

                        return actions;
                    }),
                    catchError((response: HttpErrorResponse) => {

                        let userIssueStatus: UserStatus;

                        if (response.status === 401) {
                            if (((<HttpErrorResponse>response)?.error === UserStatus.PASSWORD_RESET_REQUIRED)
                                || ((<HttpErrorResponse>response)?.error === UserStatus.TO_BE_DELETED)
                                || ((<HttpErrorResponse>response)?.error === UserStatus.ETX_LOGIN_REQUIRED)
                            ) {
                                userIssueStatus = <UserStatus>(<HttpErrorResponse>response)?.error;
                            }
                        }

                        return observableOf(new auth.AuthenticationErrorAction({error: {message: userIssueStatus || payload.errorMessage}}));
                    })
                )
            )
        )
    );

    /**
     * Send 1-time token Again.
     * @method sendCodeAgain$
     */
    sendCodeAgain$: Observable<Action | {}> = createEffect(() =>
            this.actions.pipe(
                ofType(AuthActionTypes.SEND_CODE_AGAIN),
                debounceTime(500),
                map((action: auth.AuthenticateAction) => action.payload),
                switchMap(() => this.authService.sendVerificationCodeAgain()
                    .pipe(
                        catchError(error =>
                            observableOf(new auth.AuthenticationErrorAction({error: {message: 'Resend 1-time token failed'}}))
                        )
                    ))),
        {dispatch: false}
    );

    /**
     * Authenticated user data.
     * @method authenticated$
     */
    authenticated$ = createEffect(() =>
        this.actions.pipe(
            ofType(AuthActionTypes.AUTHENTICATE_SUCCESS),
            map((action: auth.AuthenticationSuccessAction) => action.payload),
            switchMap((payload) =>
                this.authenticatedUserService.getAuthenticatedUser().pipe(
                    mergeMap((user: User) => {
                        if (payload?.etxOrgId) {
                            user.etxOrgId = payload.etxOrgId;
                        }

                        this.mixpanelService.identify(user);
                        this.mixpanelService.setPeopleData(user);
                        this.mixpanelService.trackLogin(user);

                        return [new auth.AuthenticatedSuccessAction({user: user})];
                    }),
                    catchError(error => observableOf(new auth.AuthenticatedErrorAction(error)))
                )))
    );

    refreshAuthenticatedUser$: Observable<Action> = createEffect(() =>
        this.actions.pipe(
            ofType(AuthActionTypes.AUTHENTICATED_USER_REFRESH),
            switchMap(() => this.authenticatedUserService.getAuthenticatedUser()
                .pipe(
                    map(res => new auth.AuthenticatedUserRefreshSuccessAction({user: res})),
                    catchError(error => observableOf(new auth.AuthenticatedErrorAction(error)))
                ))
        )
    );

    refreshAuthenticatedUserToken$: Observable<Action> = createEffect(() =>
        this.actions.pipe(
            ofType(AuthActionTypes.AUTHENTICATED_USER_REFRESH_TOKEN),
            switchMap(() => this.authenticatedUserService.getAuthenticatedUser()
                .pipe(
                    map(res => new auth.AuthenticatedUserRefreshTokenSuccessAction()),
                    catchError(error => observableOf(new auth.AuthenticatedErrorAction(error)))
                ))
        )
    );

    /**
     * Authenticated user extra data.
     * @method authenticatedExtra$
     */
    authenticatedExtra$ = createEffect(() =>
        this.actions.pipe(
            ofType(AuthActionTypes.AUTHENTICATED_SUCCESS),
            map((action: auth.AuthenticatedSuccessAction) => action.payload),
            switchMap(payload => this.userPreferencesService.getUserExtraData(payload.user)
                .pipe(
                    mergeMap(([userPreferences, userApplications]: [Preferences, Application[] | null, Preferences | null]) => {

                        const extra = {
                            userPreferences: userPreferences,
                            userApplications: userApplications
                        };
                        const returnActions = [];

                        returnActions.push(new auth.AuthenticatedExtraSuccessAction({extra: extra}));

                        if (this.authenticatedUserService.isVendor(payload.user)) {

                            returnActions.push(new IntegrationGettingDefaultAction(
                                {applicationId: extra.userPreferences.getPreferencesItemValue('currentApp')}));
                            returnActions.push(new auth.GetVendorFeatureTogglesAction(payload.user.orgGuid));
                            returnActions.push(new auth.GetPIIShieldLevelsConfigAction());

                        } else if (payload.user && this.authenticatedUserService.isProvider(payload.user)) {

                            returnActions.push(new auth.GetDistrictFeatureTogglesAction(payload.user.orgGuid));
                            returnActions.push(new GetSchoolYearRolloverSettingsAction(payload.user.orgGuid));
                        }

                        return returnActions;
                    }),
                    catchError(error => observableOf(new auth.AuthenticatedExtraErrorAction({error: error})))
                )
            ))
    );

    /**
     * Sign out user.
     * @method signOut$
     */
    signOut$: Observable<Action> = createEffect(() =>
        this.actions.pipe(
            ofType(AuthActionTypes.SIGN_OUT),
            throttleTime(3000),
            map((action: auth.SignOutAction) => action.payload),
            switchMap((payload) => {
                this.authenticatedUserService.setPreviousUserRole();
                const userRole = this.authenticatedUserService.previousUserRole ? this.authenticatedUserService.previousUserRole : 'null';
                localStorage.setItem('lastAuthenticatedRole', userRole);

                let logout$: Observable<string> = this.authService.logout();

                if (environment.stsServer) {
                    logout$ = observableCombineLatest([
                        this.oidcSecurityProxyService.getIsAuthenticated$(),
                        this.oidcSecurityProxyService.getUserData()
                    ]).pipe(
                        take(1),
                        switchMap(([authenticatedResult, userData]: [AuthenticatedResult, any]) => {
                            if (authenticatedResult?.isAuthenticated || (userData !== null)) {
                                let logoutETX$: Observable<any> = of(true);
                                if (!payload?.authenticateWithETX) {
                                    this.authService.logoutETX()
                                        .pipe(take(1)).subscribe();
                                    logoutETX$ = this.oidcSecurityProxyService.revokeAccessToken();
                                }

                                return logoutETX$.pipe(
                                    tap(() => this.oidcSecurityProxyService.logoffLocal()),
                                    switchMap((result) => this.authService.logout()),
                                    catchError(error => this.authService.logout())
                                );
                            } else {
                                return this.authService.logout();
                            }
                        })
                    );
                }

                return logout$
                .pipe(
                    mergeMap(res => [
                        new auth.SignOutClearDataAction(
                            new auth.SignOutSuccessAction({signOutRedirectUrl: res, authenticateWithETX: payload?.authenticateWithETX})
                        )
                    ]),
                    catchError(error => [
                        new auth.SignOutClearDataAction(new auth.SignOutErrorAction({error: error}))
                    ])
                );
            }))
    );

    /**
     * Sign out clear user data.
     * @method signOutClearData$
     */
    signOutClearData$: Observable<Action> = createEffect(() =>
        this.actions.pipe(
            ofType(AuthActionTypes.SIGN_OUT_CLEAR_DATA),
            map((action: auth.SignOutClearDataAction) => action.payload),
            mergeMap((payload: Action) => {
                this.mixpanelService.trackLogout();
                this.tokenService.removeToken();
                this.authenticatedUserService.setPreviousUserRole();
                this.matDialog.closeAll();
                this.dataBrowser.clearData();
                this.dataBrowser.clear();
                sessionStorage.clear();
                this.mixpanelService.reset();

                return [
                    new IntegrationClearState(),
                    new IntegrationClearSessionStoredData(),
                    new ApplicationClearState(),
                    new auth.SetDistrictFeatureTogglesAction([]),
                    new auth.SetVendorFeatureTogglesAction([]),
                    payload ? payload : new auth.SignOutSuccessAction(),
                ];
            }))
    );

    /**
     * @method SignOutSuccessAction$
     */
    SignOutSuccessAction$: Observable<Action> = createEffect(() =>
        this.actions.pipe(
            ofType(AuthActionTypes.SIGN_OUT_SUCCESS),
            map((action: auth.SignOutSuccessAction) => action.payload),
            mergeMap((payload: { signOutRedirectUrl?: string; authenticateWithETX?: boolean }) => {
                if (payload?.authenticateWithETX) {
                    this.authService.getXAuthToken();
                } else {
                    this.authService.redirectToLoginPage({}, payload?.signOutRedirectUrl);
                }
                return [];
            }))
    );

    /**
     * @method SignOutErrorAction$
     */
    SignOutErrorAction$: Observable<Action> = createEffect(() =>
        this.actions.pipe(
            ofType(AuthActionTypes.SIGN_OUT_ERROR),
            map((action: auth.SignOutErrorAction) => action.payload),
            mergeMap((payload: { signOutRedirectUrl?: string; error: string }) => {
                this.authService.redirectToLoginPage({}, payload?.signOutRedirectUrl);
                return [];
            }))
    );

    /**
     * Authenticate user.
     * @method authenticatedUpdateExtraUserPreferenceItem$
     */
    authenticatedUpdateExtraUserPreferenceItem$: Observable<Action> = createEffect(() =>
        this.actions.pipe(
            ofType(AuthActionTypes.AUTHENTICATED_UPDATE_EXTRA_USER_PREFERENCE_ITEM),
            map((action: auth.AuthenticatedUpdateExtraUserPreferenceItemAction) => action.payload),
            switchMap(payload => this.userPreferencesService.updatePreferencesItem(payload.key, payload.userPreferenceItem)
                .pipe(
                    map(res => new auth.AuthenticatedUpdateExtraUserPreferencesSuccessAction({userPreferences: res})),
                    catchError(error =>
                        observableOf(new auth.AuthenticatedExtraErrorAction(
                            {error: {message: 'Could not update user preferences.'}})))
                )))
    );

    /**
     * Authenticate user.
     * @method authenticatedUpdateExtraUserPreferences$
     */
    authenticatedUpdateExtraUserPreferences$: Observable<Action> = createEffect(() =>
        this.actions.pipe(
            ofType(AuthActionTypes.AUTHENTICATED_UPDATE_EXTRA_USER_PREFERENCES),
            map((action: auth.AuthenticatedUpdateExtraUserPreferencesAction) => action.payload),
            switchMap(payload => this.userPreferencesService.updatePreferences(payload.preferences)
                .pipe(
                    map(res => new auth.AuthenticatedUpdateExtraUserPreferencesSuccessAction({userPreferences: res})),
                    catchError(error =>
                        observableOf(new auth.AuthenticatedExtraErrorAction(
                            {error: {message: 'Could not update user preferences.'}})))
                )))
    );

    /**
     * Load vendor apps.
     * @method loadVendorApps$
     */
    loadVendorApps$ = createEffect(() =>
        this.actions.pipe(
            ofType(AuthActionTypes.LOAD_VENDOR_APPS),
            map((action: auth.LoadVendorApps) => action.payload),
            withLatestFrom(this.authenticatedUserService.authenticatedUser$),
            switchMap(([payload, authenticatedUser]: [{ user: User, deletingAppGuid?: string }, User]) =>
                this.userPreferencesService.loadVendorApps(payload?.user || authenticatedUser)
                    .pipe(
                        withLatestFrom(this.store.pipe(select(getAuthenticatedUserApplications))),
                        mergeMap(([newUserApplications = [], oldUserApplications = []]: [Application[], Application[]]) => {

                            const appsObj = {};
                            oldUserApplications.forEach((app: Application) => appsObj[app.guid] = app);
                            newUserApplications = newUserApplications.map((app: Application) => {
                                app.piiLevel = appsObj[app.guid]?.piiLevel;
                                app.isDeleting = (app.guid === payload?.deletingAppGuid) || (appsObj[app.guid]?.isDeleting);
                                return app;
                            });

                            const result: any[] = [new auth.UpdateUserApps({userApplications: newUserApplications})];

                            if (newUserApplications?.length === 0) {
                                result.push(new UpdateVendorCurrentAppAction({currentAppId: ''}));
                            }

                            return result;
                        }),
                        catchError(error => observableOf(new auth.AuthenticatedExtraErrorAction({error: error})))
                    )
            ))
    );

    /**
     * Update user's apps.
     * @method updateUserApps$
     */
    updateUserApps$ = createEffect(() =>
        this.actions.pipe(
            ofType(AuthActionTypes.UPDATE_USER_APPS),
            map((action: auth.UpdateUserApps) => action.payload),
            mergeMap(payload => [new auth.AuthenticatedExtraSuccessAction({extra: payload})]),
            catchError(error => observableOf(new auth.AuthenticatedExtraErrorAction({error: error})))
        )
    );

    updateAuthState = createEffect(() =>
        fromEvent<StorageEvent>(window, 'storage').pipe(
            // listen for state changes
            filter(evt => evt.key === 'auth'),
            filter(evt => evt.newValue !== null),
            distinctUntilChanged((prev: StorageEvent, curr: StorageEvent) => prev && curr && prev.newValue === curr.newValue),
            debounceTime(3000),
            map(evt => {
                const localStorageKeys = {
                    auth: 'auth'
                };

                const changedStateKey = localStorageKeys[evt.key];
                // deserialize the new state from local storage
                const newState = JSON.parse(localStorage.getItem(changedStateKey));

                if (changedStateKey === localStorageKeys.auth && newState['userPreferences']) {
                    newState.userPreferences = new Preferences(newState.userPreferences);
                }

                // dispatch an update state action
                // with the new state as payload
                return new auth.UpdateStateFromStorageAction({changedStateKey: changedStateKey, changedStateValue: newState});
            })
        )
    );

    updateIntegrationState = createEffect(() =>
        fromEvent<StorageEvent>(window, 'storage').pipe(
            // listen for state changes
            filter(evt => evt.key === 'integration'),
            filter(evt => evt.newValue !== null),
            distinctUntilChanged((prev: StorageEvent, curr: StorageEvent) => prev && curr && prev.newValue === curr.newValue),
            map(evt => {
                const localStorageKeys = {
                    integration: 'integration'
                };

                const changedStateKey = localStorageKeys[evt.key];
                // deserialize the new state from local storage
                const newState = JSON.parse(localStorage.getItem(changedStateKey));

                // dispatch an update state action
                // with the new state as payload
                return new auth.UpdateStateFromStorageAction({changedStateKey: changedStateKey, changedStateValue: newState});
            })
        )
    );

    GetUserOrganizationAction$: Observable<Action | {}> = createEffect(() =>
        this.actions.pipe(
            ofType(AuthActionTypes.GET_USER_ORGANIZATION),
            map((action: auth.GetUserOrganizationAction) => action.payload),
            withLatestFrom(
                this.store.pipe(select(getAuthenticatedUser)),
                this.store.pipe(select(getUserOrganization)),
            ),
            switchMap(([orgId, user, organization]: [string, User, DirectoryOrganization]) =>
                ((organization && !this.authenticatedUserService.isSysAdmin(user))
                    ? observableOf(organization)
                    : this.orgService.getOrg(orgId || user.orgGuid))
                        .pipe(
                            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
                            mergeMap(([org, applicationSettingsState]: [DirectoryOrganization, fromApplicationSettings.State]) => {
                                const result: any[] = [new auth.SetUserOrganizationAction(org)];

                                // do not save sys admin organization
                                if ((orgId || !this.authenticatedUserService.isSysAdmin(user))
                                    && (!organization
                                && !this.authenticatedUserService.isVendor(user)
                                && (applicationSettingsState?.defaultApplications?.length > 0))) {

                                let defaultApplications: Application[] = [...applicationSettingsState.defaultApplications];

                                // Filter Google Classroom Export for organizations where host !== 'coursera.gg4l.com' || 'qwiklabs.gg4l.com'
                                defaultApplications = ApplicationSettingsService.filterApplicationModesForGoogleClassroom(
                                    defaultApplications, org?.host
                                );

                                result.push(
                                    {
                                        type: ApplicationSettingsActions.SET_DEFAULT_APPLICATIONS,
                                        payload: defaultApplications
                                    }
                                );

                                if (this.authenticatedUserService.isVendor(user)) {
                                    result.push(
                                        {
                                            type: ApplicationSettingsActions.SET_VENDOR_ALLOWED_APPLICATIONS,
                                            payload: [...defaultApplications]
                                        }
                                    );
                                } else {
                                    result.push(
                                        {
                                            type: ApplicationSettingsActions.SET_PROVIDER_ALLOWED_APPLICATIONS,
                                            payload: [...defaultApplications]
                                        }
                                    );
                                }
                            }

                                return result;
                            }),
                            catchError(error =>
                                observableOf(new auth.AuthenticatedExtraErrorAction(
                                    {error: {message: 'Could not get organization.'}})))
                        ))
        )
    );

    getDistrictFeatureToggles$ = createEffect(() =>
        this.actions.pipe(
            ofType(AuthActionTypes.GET_DISTRICT_FEATURE_TOGGLES),
            map((action: auth.GetDistrictFeatureTogglesAction) => action),
            concatMap((action: { payload: string }) => this.featureTogglesService.getDistrictFeatureToggles(action.payload)
                .pipe(
                    mergeMap((featureToggles: FeatureToggleModel[]) => [new auth.SetDistrictFeatureTogglesAction(featureToggles)]),
                    catchError(error => observableOf(new auth.GetDistrictFeatureTogglesFailedAction({error: error})))
                ))
        ));

    getVendorFeatureToggles$ = createEffect(() =>
        this.actions.pipe(
            ofType(AuthActionTypes.GET_VENDOR_FEATURE_TOGGLES),
            map((action: auth.GetVendorFeatureTogglesAction) => action),
            concatMap((action: { payload: string }) => this.featureTogglesService.getVendorFeatureToggles(action.payload)
                .pipe(
                    mergeMap((featureToggles: FeatureToggleModel[]) => [new auth.SetVendorFeatureTogglesAction(featureToggles)]),
                    catchError(error => observableOf(new auth.GetVendorFeatureTogglesFailedAction({error: error})))
                ))
        ));

    getPIIShieldLevelsConfigAction$ = createEffect(() =>
        this.actions.pipe(
            ofType(AuthActionTypes.GET_PII_SHIELD_LEVELS_CONFIG),
            map((action: auth.GetPIIShieldLevelsConfigAction) => action),
            concatMap((action: auth.GetPIIShieldLevelsConfigAction) => this.piiShieldService.getPIIShieldLevelsConfig()
                .pipe(
                    mergeMap((piiShieldLevelsConfig: PIIShieldLevelConfig[]) => [
                        new auth.SetPIIShieldLevelsConfigAction(piiShieldLevelsConfig)
                    ]),
                    catchError(error =>
                        observableOf(new auth.AuthenticatedExtraErrorAction({error: {message: 'Could not get Privacy Shield config.'}})))
                ))
        ));

    getPassportLoginIDPListAction$: Observable<Action | null> = createEffect(() =>
        this.actions.pipe(
            ofType(AuthActionTypes.GET_PASSPORT_LOGIN_IDP_LIST),
            withLatestFrom(
                this.ssoPortalService.getETXOrgId().pipe(catchError(error => of()))
            ),
            switchMap(([action, etrxOrgId]: [auth.GetPassportLoginIDPListAction, number | null]) =>
                !etrxOrgId
                    ? observableOf(null)
                    : this.ssoPortalService.getPassportLoginIDPs(etrxOrgId)
                        .pipe(
                            mergeMap((passportLoginIDPs: PassportLoginIDP[]) =>
                                <any[]>[new auth.SetPassportLoginIDPListAction(passportLoginIDPs)]
                            ),
                            catchError(error =>
                                observableOf([
                                    new auth.AuthenticatedExtraErrorAction({error: {message: 'Could not get organization.'}}),
                                    new auth.SetPassportLoginIDPListAction(null)
                                ])
                            )
                        ))
        )
    );

    getOnboardingWizardCompletedStepsAction$ = createEffect(() =>
        this.actions.pipe(
            ofType(AuthActionTypes.GET_ONBOARDING_WIZARD_COMPLETED_STEPS),
            map((action: auth.GetOnboardingWizardCompletedStepsAction) => action),
            concatMap((action: auth.GetOnboardingWizardCompletedStepsAction) =>
                this.applicationsService.getOnboardingWizardCompletedSteps(action.payload)
                    .pipe(
                        mergeMap((completedSteps: string[]) => [
                            new auth.SetOnboardingWizardCompletedStepsAction(completedSteps)
                        ]),
                        catchError(error =>
                            observableOf(new auth.GetOnboardingWizardCompletedStepsFailedAction({error: error})))
                    ))
        ));

    /**
     * @constructor
     * @paramactions
     * @param authService
     * @param authenticatedUserService
     * @param dataBrowser
     * @param userPreferencesService
     * @param tokenService
     * @param store
     * @param matDialog
     * @param oidcSecurityProxyService
     */
    constructor(private actions: Actions,
                private authService: AuthenticationService,
                public authenticatedUserService: AuthenticatedUserService,
                private dataBrowser: DataBrowser,
                private userPreferencesService: UserPreferencesService,
                private tokenService: TokenService,
                private store: Store<State>,
                private matDialog: MatDialog,
                private oidcSecurityProxyService: OidcSecurityProxyService,
                private orgService: OrgsService,
                private featureTogglesService: FeatureTogglesService,
                private piiShieldService: PIIShieldService,
                private ssoPortalService: SSOPortalService,
                private mixpanelService: MixpanelService,
                private applicationsService: ApplicationsService
    ) {
    }
}
